<template>
  <ak-card-header class="flex flex--center">
    <div class="flex__item flex flex--column">
      <h4 class="color-grey mb-0">Campaign Proposals</h4>
      <small v-if="!loading && campaigns.length"
        >Showing top {{ campaigns.length }}</small
      >
    </div>
    <ak-button
      v-if="!loading"
      color="primary-light"
      size="small"
      tag="router-link"
      :to="{ name: 'allBriefs' }"
    >
      View More
    </ak-button>
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column is-scrollable h-full">
    <ak-display-table v-if="loading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell>
          <ak-pack>
            <ak-pack-item>
              <div class="c-faux-text width-75" />
              <div>
                <p class="c-faux-text width-100 mb-none"></p>
              </div>
              <div>
                <p class="c-faux-text width-150 mb-none"></p>
              </div>
            </ak-pack-item>
          </ak-pack>
        </ak-display-table-cell>
        <ak-display-table-cell class="cell--1">
          <div class="c-faux-text width-50" />
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
    <ak-empty-state
      v-show="!loading && !campaigns.length"
      title="Campaigns"
      icon="list"
      fa
      fa-light
    >
      There are currently no Campaigns to show
      <span v-if="selectedUser"> for the selected user </span>
      <span v-if="selectedAccount"> under this account </span>
    </ak-empty-state>
    <ak-table
      v-if="!loading && campaigns.length"
      v-model:data="campaignsList"
      hoverable
      hide-head
      background
      spacing="medium"
    >
      <template #default="{ row }">
        <ak-table-column :class="getBgColor(row)">
          <router-link
            :to="{ name: 'singleCampaign', params: { quote: row._id } }"
          >
            <ak-pack :alignment="'top'">
              <ak-pack-item class="flex flex--column">
                <!-- Icon -->
                <ak-tooltip
                  :placement="'right'"
                  :has-arrow="false"
                  class="mb-tiny"
                >
                  <template #trigger>
                    <div
                      class="flex--self-start mt-icon-fix-1 circle--status-icons"
                      :class="row._additions.icon.outer"
                    >
                      <ak-icon
                        class="circle--font-small"
                        :class="row._additions.icon.inner"
                      ></ak-icon>
                    </div>
                  </template>
                  {{ row._additions.icon.description }}
                </ak-tooltip>
                <!-- Stage -->
                <ak-tooltip :placement="'right'" :has-arrow="false">
                  <template #trigger>
                    <div
                      class="mr-small flex--self-start mt-icon-fix-1 circle circle--status-icons bg-grey-lighter"
                    >
                      <div class="circle--font-small text-bold color-grey">
                        {{ row._additions.stageNumber }}
                      </div>
                    </div>
                  </template>
                  {{ row._additions.stage }}
                </ak-tooltip>
              </ak-pack-item>
              <ak-pack-item class="flex__item">
                <div class="text-bold color-black flex">
                  <!-- Name -->
                  <ak-tooltip
                    v-if="row.data.name.length > 25"
                    :placement="'bottom'"
                  >
                    <template #trigger>
                      {{ row.data.name.substring(0, 25) + '..' }}</template
                    >
                    {{ row.data.name }}
                  </ak-tooltip>
                  <div v-if="row.data.name.length <= 25">
                    {{ row.data.name }}
                  </div>
                  <!-- Agency Only Label -->
                  <ak-tooltip
                    v-if="row.agencyOnly"
                    :placement="'right'"
                    :has-arrow="false"
                    class="ml-tiny"
                  >
                    <template #trigger>
                      <ak-tag :size="'small'" :color="'primary'" ghost nowrap>
                        AO
                      </ak-tag>
                    </template>
                    Agency Only
                  </ak-tooltip>
                  <!-- Status -->
                  <ak-tag
                    v-if="
                      row._additions.status.tag &&
                      row._additions.status.tag.text
                    "
                    :size="'small'"
                    class="ml-auto mr-tiny"
                    :color="getTagColor(row)"
                    nowrap
                  >
                    {{
                      row._additions.status
                        ? row._additions.status.tag.text
                        : ''
                    }}
                  </ak-tag>
                </div>
                <div class="text-small text-bold mb-tiny">
                  {{ row.account.name }}
                </div>
                <div class="flex flex--center">
                  <ak-tag
                    :size="'small'"
                    :color="'secondary'"
                    class="mr-tiny"
                    nowrap
                  >
                    {{ row._additions.dateRange }}
                  </ak-tag>
                  <ak-tag
                    :size="'small'"
                    :color="'info'"
                    class="text-monospace"
                    nowrap
                  >
                    {{ getCurrencyCode(row.data.currency) }}
                    {{ row.data.budget }}
                  </ak-tag>
                </div>
              </ak-pack-item>
            </ak-pack>
          </router-link>
        </ak-table-column>
      </template>
    </ak-table>
  </ak-card-body>
</template>

<script>
import { defineComponent, ref, watch } from 'vue'
import { currencyCodeToSymbol } from '@akerolabs/akero-vue/src/filters/currency'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import DisplayTable from '@akerolabs/akero-vue/src/components/displayTable/DisplayTable.vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import Table from '@akerolabs/akero-vue/src/components/table/Table.vue'
import TableColumn from '@akerolabs/akero-vue/src/components/table/TableColumn.vue'
import RouterLink from '../../../legacy/router/RouterLink.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Tooltip from '@akerolabs/akero-vue/src/components/tooltip/Tooltip.vue'
import Tag from '@akerolabs/akero-vue/src/components/tag/Tag.vue'

export default defineComponent({
  name: 'AkWidgetCampaignProposals',

  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [DisplayTable.name]: DisplayTable,
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [RouterLink.name]: RouterLink,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Tooltip.name]: Tooltip,
    [Tag.name]: Tag,
  },

  props: {
    /**
     * @type {Array}
     * @description List of campaigns
     */
    campaigns: {
      type: Array,
      default: () => [],
    },
    /**
     * @type {Boolean}
     * @description Check loading state
     */
    loading: {
      type: Boolean,
      default: true,
    },
    /**
     * @type {String}
     * @description Selected User Id
     */
    selectedUser: {
      type: String,
      default: undefined,
    },
    /**
     * @type {String}
     * @description Selected Account Id
     */
    selectedAccount: {
      type: String,
      default: undefined,
    },
  },

  setup(props) {
    let n = 10
    const campaignsList = ref(props.campaigns)

    /**
     * @param {String} str
     * @return {String}
     * @description Function to convert currency string to symbol
     */
    const getCurrencyCode = (str) => {
      return currencyCodeToSymbol(str)
    }

    /**
     * @param {Object}
     * @return {String}
     * @description Gets angular label class and converts to tag color
     */
    const getTagColor = (tag) => {
      if (
        tag._additions.status &&
        tag._additions.status.tag &&
        tag._additions.status.tag.colour
      ) {
        return tag._additions.status.tag.colour.split('--').pop()
      }
    }

    /**
     * @param {Object}
     * @return {String}
     * @description Gets background color for row if applicable
     */
    const getBgColor = (row) => {
      if (row._additions.status && row._additions.status.background) {
        return row._additions.status.background
      }
    }

    watch(
      () => props.campaigns,
      (value) => {
        campaignsList.value = value
      },
      { deep: true },
    )

    return {
      n,
      getCurrencyCode,
      getTagColor,
      getBgColor,
      campaignsList,
    }
  },
})
</script>
